import clsx from "clsx";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { AutomationRun } from "@/lib/types";

import { useCancelAutomationRun } from "@/hooks/useAutomations";
import { useStore } from "@/lib/store";

import { relativeTime, formatDuration } from "./utils";

interface AutomationRunListProps {
  automationId: string;
  runs: AutomationRun[];
}

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-cyber-green/10 text-cyber-green border-cyber-green/30",
  failed: "bg-cyber-red/10 text-cyber-red border-cyber-red/30",
  running: "bg-cyber-cyan/10 text-cyber-cyan border-cyber-cyan/30",
  skipped: "bg-cyber-muted/10 text-cyber-muted border-cyber-border",
  cancelled: "bg-cyber-muted/10 text-cyber-muted border-cyber-border",
};

export function AutomationRunList({ automationId, runs }: AutomationRunListProps): JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const cancelRun = useCancelAutomationRun();
  const showToast = useStore((state) => state.showToast);

  async function handleCancel(runId: string): Promise<void> {
    try {
      const result = await cancelRun.mutateAsync({ automationId, runId });
      showToast(
        result.run.status === "cancelled"
          ? "Automation run cancelled"
          : `Run is ${result.run.status}`,
        "success",
      );
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to cancel run", "error");
    }
  }

  if (runs.length === 0) {
    return <div className="py-8 text-center text-sm text-cyber-muted">No runs yet</div>;
  }

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="grid grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)_64px_48px] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-cyber-muted">
        <span>Time</span>
        <span>Status</span>
        <span>Response</span>
        <span className="text-right">Duration</span>
        <span className="text-right">Action</span>
      </div>

      {/* Rows */}
      {runs.map((run) => (
        <div key={run.id}>
          <div
            className={clsx(
              "relative rounded transition-colors hover:bg-cyber-surface/50",
              expandedId === run.id && "bg-cyber-surface/50",
            )}
          >
            <button
              type="button"
              className="grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_72px_minmax(0,1fr)_64px_48px] items-center gap-2 px-3 py-2 text-left text-sm"
              onClick={() => setExpandedId(expandedId === run.id ? null : run.id)}
            >
              <span className="truncate text-xs text-cyber-text">
                {relativeTime(run.startedAt)}
              </span>
              <span>
                <span
                  className={`rounded border px-1.5 py-0.5 text-[10px] ${
                    STATUS_STYLES[run.status] ?? STATUS_STYLES.skipped
                  }`}
                >
                  {run.status}
                </span>
              </span>
              <span className="truncate text-xs text-cyber-muted">
                {run.response
                  ? run.response.slice(0, 80) + (run.response.length > 80 ? "..." : "")
                  : run.errorMessage
                    ? run.errorMessage.slice(0, 80)
                    : "—"}
              </span>
              <span className="text-right text-xs text-cyber-muted">
                {formatDuration(run.startedAt, run.completedAt)}
              </span>
              <span />
            </button>
            {run.status === "running" && (
              <button
                type="button"
                title="Cancel run"
                disabled={cancelRun.isPending && cancelRun.variables?.runId === run.id}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded px-1 py-0.5 text-[10px] text-cyber-muted transition-colors hover:bg-cyber-red/10 hover:text-cyber-red disabled:cursor-wait disabled:opacity-50"
                onClick={() => void handleCancel(run.id)}
              >
                Cancel
              </button>
            )}
          </div>

          {/* Expanded response */}
          {expandedId === run.id && run.response && (
            <div className="mx-3 mb-2 rounded border border-cyber-border bg-cyber-bg p-3 text-sm">
              <div className="prose prose-sm prose-invert max-w-none text-cyber-text [&_a]:text-cyber-cyan [&_code]:text-cyber-magenta [&_pre]:border [&_pre]:border-cyber-border [&_pre]:bg-cyber-surface">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{run.response}</ReactMarkdown>
              </div>
            </div>
          )}

          {expandedId === run.id && run.errorMessage && !run.response && (
            <div
              className={clsx(
                "mx-3 mb-2 rounded border p-3 text-sm",
                run.status === "cancelled"
                  ? "border-cyber-border bg-cyber-muted/5 text-cyber-muted"
                  : "border-cyber-red/20 bg-cyber-red/5 text-cyber-red",
              )}
            >
              {run.errorMessage}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
